import moment from 'moment';
import { FC } from 'react';
import constants from '../../../constants/constants';
import MIXTLogo from '../../../assets/images/logo.png';
import { formatCost, truncateText } from '../../../helpers/utils';
import { MixtLineItemsTableChromeEmpty, MixtLineItemsThead } from './thermal/mixtLineItemsTableFragments';
import PackingSlipPeelTab from './thermal/PackingSlipPeelTab';
import { PackingSlipPrintTableShell, PACKING_SLIP_INNER_SEPARATE_TABLE_STYLE } from './thermal/packingSlipPrintTableShell';

type SlipVariant =
    | 'default'
    | 'chrome-header'
    | 'chrome-footer'
    | 'chrome-footer-peel-only'
    | 'chrome-continuation-header';

interface IPrintPackaginSlipMIXT {
    orderDetails: any,
    slipVariant?: SlipVariant,
    continuationPage?: boolean,
    showOrderTotals?: boolean,
}

const RenderInnerHTML = (props: any) => (<span dangerouslySetInnerHTML={{ __html: props.HTML }}></span>);

const PrintPackaginSlipMIXT: FC<IPrintPackaginSlipMIXT> = ({
    orderDetails,
    slipVariant = 'default',
    continuationPage = false,
    showOrderTotals = true,
}) => {
    if (slipVariant === 'chrome-footer') {
        return (
            <PackingSlipPrintTableShell>
                    <tbody style={{ verticalAlign: 'top' }}>
                        <tr>
                            <td style={{ padding: '8px 0' }}>
                                <table
                                    aria-hidden="true"
                                    style={PACKING_SLIP_INNER_SEPARATE_TABLE_STYLE}
                                >
                                    <tbody>
                                        <tr>
                                            <td
                                                colSpan={6}
                                                style={{
                                                    padding: 4,
                                                    fontSize: 12,
                                                    fontWeight: "bold",
                                                    textAlign: "right",
                                                }}
                                            >
                                                Total: ${formatCost(orderDetails?.order_total)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                    <PackingSlipPeelTab orderNumber={orderDetails?.order_number} />
            </PackingSlipPrintTableShell>
        );
    }

    if (slipVariant === 'chrome-footer-peel-only') {
        return (
            <PackingSlipPrintTableShell>
                    <PackingSlipPeelTab orderNumber={orderDetails?.order_number} />
            </PackingSlipPrintTableShell>
        );
    }

    if (slipVariant === 'chrome-continuation-header') {
        return (
            <PackingSlipPrintTableShell>
                    <tbody>
                        <tr>
                            <td style={{ padding: '4px 0 0', verticalAlign: 'top' }}>
                                <MixtLineItemsTableChromeEmpty />
                            </td>
                        </tr>
                    </tbody>
            </PackingSlipPrintTableShell>
        );
    }

    if (slipVariant === 'default' && continuationPage) {
        const lineItems = orderDetails?.order_item_info ?? [];
        if (showOrderTotals && lineItems.length === 0) {
            return (
                <PackingSlipPrintTableShell>
                    <tbody>
                        <tr>
                            <td style={{ padding: '8px 0', verticalAlign: 'top' }}>
                                <table style={PACKING_SLIP_INNER_SEPARATE_TABLE_STYLE}>
                                    <tbody>
                                        <tr>
                                            <td
                                                colSpan={6}
                                                style={{
                                                    padding: 4,
                                                    fontSize: 12,
                                                    fontWeight: 'bold',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                Total: ${formatCost(orderDetails?.order_total)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                    <PackingSlipPeelTab orderNumber={orderDetails?.order_number} />
                </PackingSlipPrintTableShell>
            );
        }
        return (
            <PackingSlipPrintTableShell>
                    <tbody>
                        <tr>
                            <td style={{ padding: '4px 0 0', verticalAlign: 'top' }}>
                                <table style={PACKING_SLIP_INNER_SEPARATE_TABLE_STYLE}>
                                    <MixtLineItemsThead />
                                    <tbody>
                                        {lineItems.map((item: any, index: number) => (
                                            <tr key={index} style={{ pageBreakInside: 'avoid' }}>
                                                <td style={{ padding: "4px", fontWeight: "normal", borderBottom: '1px solid #000' }}>{item.master_sku}</td>
                                                <td style={{ padding: "4px", fontWeight: "normal", borderBottom: '1px solid #000' }}>{item?.product_name ? truncateText(item?.product_name, 100) : "-"}</td>
                                                <td style={{ padding: "4px", textAlign: "right", fontWeight: "normal", borderBottom: '1px solid #000' }}>{item?.quantity}</td>
                                                <td style={{ padding: "4px", textAlign: "right", fontWeight: "normal", borderBottom: '1px solid #000' }}>{formatCost(item?.unit_cost)}</td>
                                                <td style={{ padding: "4px", textAlign: "right", fontWeight: "normal", borderBottom: '1px solid #000' }}>{formatCost('0.00')}</td>
                                                <td style={{ padding: "4px", textAlign: "right", fontWeight: "normal", borderBottom: '1px solid #000' }}>{formatCost(item?.total_cost)}</td>
                                            </tr>
                                        ))}
                                        {showOrderTotals && (
                                            <tr>
                                                <td colSpan={6} style={{ padding: 4, fontSize: 12, fontWeight: "bold", textAlign: "right" }}>
                                                    Total: ${formatCost(orderDetails?.order_total)}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                    <PackingSlipPeelTab orderNumber={orderDetails?.order_number} />
            </PackingSlipPrintTableShell>
        );
    }

    const showLineBlock = slipVariant === 'default';

    return (
        <PackingSlipPrintTableShell>
                <thead style={{ verticalAlign: 'top' }}>
                    {/* Header */}
                    <tr>
                        <td
                            style={{
                                background: "#FFFFFF",
                                borderBottom: "3px solid #005C99",
                                padding: "8px 4px",
                                textAlign: "center"
                            }}
                        >
                            <table
                                style={{
                                    fontFamily: '"Source Sans Pro", sans-serif',
                                    fontSize: 12,
                                    margin: "0 auto",
                                    padding: 0,
                                    border: 0,
                                    textAlign: "left",
                                    borderCollapse: "collapse",
                                    width: "100%",
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ width: 80, verticalAlign: "middle" }}>
                                            <img
                                                src={MIXTLogo}
                                                alt="MIXT Logo"
                                                width={80}
                                                height={28}
                                                style={{ border: 0, display: "block" }}
                                            />
                                        </th>
                                        <th style={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <h1
                                                style={{
                                                    margin: 0,
                                                    fontWeight: "bold",
                                                    fontSize: 18,
                                                    color: "#005C99"
                                                }}
                                            >
                                                Packing Slip
                                            </h1>
                                        </th>
                                        <th
                                            style={{
                                                width: 80,
                                                textAlign: "right",
                                                verticalAlign: "middle"
                                            }}
                                        >
                                            &nbsp;
                                        </th>
                                    </tr>
                                </thead>
                            </table>
                        </td>
                    </tr>
                </thead>
                <tbody style={{ verticalAlign: 'top' }}>
                    {/* Body Content */}
                    <tr>
                        <td style={{ padding: "4px 0 0" }}>
                            <table
                                aria-hidden="true"
                                style={{
                                    fontSize: 12,
                                    margin: "0 auto",
                                    padding: 0,
                                    border: 0,
                                    textAlign: "left",
                                    borderCollapse: "collapse",
                                    width: "100%"
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td style={{ padding: '4px 0 0' }}>
                                            <table
                                                aria-hidden="true"
                                                style={{
                                                    margin: 0,
                                                    padding: 0,
                                                    textAlign: "left",
                                                    borderCollapse: "collapse",
                                                    width: "100%",
                                                    tableLayout: "fixed",
                                                }}
                                            >
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            style={{
                                                                padding: 4,
                                                                verticalAlign: "top",
                                                            }}
                                                        >
                                                            <h5
                                                                style={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    color: "#005C99",
                                                                    marginBottom: 4
                                                                }}
                                                            >
                                                                Shipping Details
                                                            </h5>
                                                            <div
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: "bold",
                                                                    marginBottom: 2
                                                                }}
                                                            >
                                                                {orderDetails?.recipient?.name}
                                                            </div>
                                                            <div style={{ marginBottom: 6 }}>
                                                                <RenderInnerHTML HTML={orderDetails?.recipient?.address + ', ' + (orderDetails?.recipient?.address_2 ? orderDetails?.recipient?.address_2 + ', ' : '') + '<br />' + orderDetails?.recipient?.city + ', ' + orderDetails?.recipient?.state + ', ' + orderDetails?.recipient?.country + ', ' + orderDetails?.recipient?.zip_code} /> <br />
                                                                {orderDetails?.recipient?.phone}
                                                            </div>
                                                        </td>

                                                        <td
                                                            style={{
                                                                padding: 4,
                                                                verticalAlign: "top",
                                                                textAlign: "right",
                                                            }}
                                                        >
                                                            <h5
                                                                style={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    color: "#005C99",
                                                                    marginBottom: 4
                                                                }}
                                                            >
                                                                Order Details
                                                            </h5>

                                                            <div>
                                                                Order No:&nbsp;
                                                                <span
                                                                    style={{
                                                                        fontSize: 12,
                                                                        marginBottom: 2,
                                                                        fontWeight: "bold"
                                                                    }}
                                                                >
                                                                    {orderDetails?.order_number}
                                                                </span>
                                                            </div>
                                                            {orderDetails?.order_date &&
                                                                <div>
                                                                    Order Date:&nbsp;
                                                                    <span
                                                                        style={{
                                                                            fontSize: 12,
                                                                            fontWeight: "bold",
                                                                            marginBottom: 2
                                                                        }}
                                                                    >
                                                                        {orderDetails?.order_date ? moment(new Date(orderDetails?.order_date)).format(constants.datetimeFormats.date) : ''}
                                                                    </span>
                                                                </div>
                                                            }
                                                            {orderDetails?.shipment_date && orderDetails?.status === 4 &&
                                                                <div>
                                                                    Shipped Date:&nbsp;
                                                                    <span
                                                                        style={{
                                                                            fontSize: 12,
                                                                            fontWeight: "bold",
                                                                            marginBottom: 2
                                                                        }}
                                                                    >
                                                                        {orderDetails?.shipment_date ? moment(new Date(orderDetails?.shipment_date)).format(constants.datetimeFormats.date) : ''}
                                                                    </span>
                                                                </div>
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: "4px 0 0" }}>
                                            <table
                                                style={{
                                                    margin: 0,
                                                    padding: 0,
                                                    textAlign: "left",
                                                    borderCollapse: "separate",
                                                    width: "100%",
                                                    borderSpacing: '2px',
                                                }}
                                            >
                                                <MixtLineItemsThead />
                                                <tbody>
                                                    {showLineBlock && orderDetails?.order_item_info?.map((item: any, index: number) => {
                                                        return <tr key={index} style={{pageBreakInside: 'avoid'}}>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {item.master_sku}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {item?.product_name ? truncateText(item?.product_name, 100) : "-"}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    textAlign: "right",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {item?.quantity}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    textAlign: "right",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {formatCost(item?.unit_cost)}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    textAlign: "right",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {formatCost('0.00')}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "4px",
                                                                    textAlign: "right",
                                                                    fontWeight: "normal",
                                                                    borderBottom: '1px solid #000',
                                                                }}
                                                            >
                                                                {formatCost(item?.total_cost)}
                                                            </td>
                                                        </tr>
                                                    })}
                                                    {showLineBlock && showOrderTotals && (
                                                    <tr>
                                                        <td
                                                            colSpan={6}
                                                            style={{
                                                                padding: 4,
                                                                fontSize: 12,
                                                                fontWeight: "bold",
                                                                textAlign: "right",
                                                            }}
                                                        >
                                                            Total: ${formatCost(orderDetails?.order_total)}
                                                        </td>
                                                    </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
                {showLineBlock && <PackingSlipPeelTab orderNumber={orderDetails?.order_number} />}
        </PackingSlipPrintTableShell>
    );
}

export default PrintPackaginSlipMIXT;